import { Component, h, createRef } from 'preact';
import cx from 'classnames';

import { Listing } from 'listing.model';
import { InquiryDocumentType } from '../../../../../constants/inquiries';
import { Inquiry, InquiryDocument } from '../../../../../types/inquiries';
import { ReqState } from '../../../../../types';

import { HopplerModalizer } from '../../common/hoppler-modalizer.component';
import { InqDocumentNamesForm } from './inq-document-names-form.component';
import { InqDocumentTermsForm } from './inq-document-terms-form.component';
import { InqDocumentRequestsForm } from './inq-document-requests-form.component';
import { divCatUtils } from '../../../../../clientside/utils/div-cat-utils';
import { InqDocumentModalTabs, DocumentTabID } from './inq-document-modal-tabs.component';


export enum InquiryDocumentPropertySpecialOptions {
  EMPTY = '0',
  CONTINUE_WITHOUT_PROPERTY = 'CONTINUE_WITHOUT_PROPERTY',
}

interface InqDocumentModalProps {
  inquiry: Inquiry;

  documentType: InquiryDocumentType;
  propertyChoices?: Listing[];
  selectedProperty?: Listing;

  contractTemplates?: InquiryDocument[];
  selectedTemplateKey?: string;

  continuingWithoutProperty: boolean;
  document: InquiryDocument;

  saveState?: ReqState;

  onDocumentChange: (document: InquiryDocument) => void;
  onOfferTemplateSelectionChange?: (property: Listing) => void;
  onContractTemplateSelectionChange?: (key: string) => void;
  onSave: (document: InquiryDocument) => void;
  onPrint: () => void;
  onClose: () => void;
}

interface InqDocumentModalState {
  activeTab?: DocumentTabID;
}

export class InqDocumentModal extends Component<InqDocumentModalProps, InqDocumentModalState> {
  formScrollerRef = createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.setState({ activeTab: DocumentTabID.NAMES });
  }

  onTabClick(tab: DocumentTabID) {
    this.setState({ activeTab: tab });

    if (this.formScrollerRef.current) {
      const scroller = this.formScrollerRef.current as HTMLDivElement;
      scroller.scrollTo(0, 0);
    }
  }

  onPropertySelectionChange(event: Event) {
    const dropdown = event.target as HTMLSelectElement;
    const propertyKey = dropdown.value;
    const props = this.props;
    
 
    if (dropdown.value === InquiryDocumentPropertySpecialOptions.CONTINUE_WITHOUT_PROPERTY) {
      props.onOfferTemplateSelectionChange(null);
    } else {
      const property = props.propertyChoices.find(property => property.propertyKey === propertyKey);
      this.props.onOfferTemplateSelectionChange(property);
    }
  }

  async onContractTemplateSelectionChange(event: Event) {
    const dropdown = event.target as HTMLSelectElement;
    const key = dropdown.value;
    const props = this.props;

    props.onContractTemplateSelectionChange(key);
  }

  isEditingExistingDocument() {
    const doc = this.props.document;
    return doc && !!(doc.loiKey || doc.contractKey);
  }

  getModalTitle() {
    const documentType = this.props.documentType;
    const isEditing = this.isEditingExistingDocument();

    if (documentType === InquiryDocumentType.OFFER) {
      return `${isEditing ? 'Edit' : 'Create'} Offer`;
    } else {
      return `${isEditing ? 'Edit' : 'Create'} Contract`;
    }
  }

  onNextClick() {
    switch (this.state.activeTab) {
      case DocumentTabID.NAMES:
        this.onTabClick(DocumentTabID.TERMS);
        break;
      case DocumentTabID.TERMS:
        this.onTabClick(DocumentTabID.REQUESTS);
        break;
      case DocumentTabID.REQUESTS:
      default:
        return;
    }
  }

  getTabContent(tab: DocumentTabID) {
    const props = this.props;
    const state = this.state;

    switch (tab) {
      case DocumentTabID.NAMES:
        return <InqDocumentNamesForm document={ this.props.document }
          documentType={ this.props.documentType }
          onDocumentChange={ this.props.onDocumentChange }
          onNextClick={ () => this.onNextClick() }
          
          activeTab={ state.activeTab }
          onTabClick={ tabId => this.onTabClick(tabId) } />;
      case DocumentTabID.TERMS:
        return <InqDocumentTermsForm document={ this.props.document }
          documentType={ this.props.documentType }
          saveState={ this.props.saveState }
          divCat={ divCatUtils.fromCompatibleObject(this.props.document) }
          continuingWithoutProperty={ this.props.continuingWithoutProperty }
          onDocumentChange={ this.props.onDocumentChange }
          onNextClick={ () => this.onNextClick() }

          onSaveClick={ () => this.props.onSave(this.props.document) }
          onPrintClick={ this.props.onPrint }

          activeTab={ state.activeTab }
          onTabClick={ tabId => this.onTabClick(tabId) } />;
      case DocumentTabID.REQUESTS:
        return <InqDocumentRequestsForm document={ this.props.document }
          saveState={ this.props.saveState }
          onDocumentChange={ this.props.onDocumentChange }
          onSaveClick={ () => this.props.onSave(this.props.document) }
          onPrintClick={ this.props.onPrint }
          
          activeTab={ state.activeTab }
          onTabClick={ tabId => this.onTabClick(tabId) }/>;
      default:
        return null;
    }
  }

  getTabbedContainer() {
    const state = this.state;
    const selectedProperty = this.props.selectedProperty;

    const offerConditions = selectedProperty || this.isEditingExistingDocument() || this.props.continuingWithoutProperty;
    const contractConditions = !!this.props.document

    if (offerConditions || contractConditions) {
      return (
        // <div className="hi-docmodal__tabbed-container">
        //   <InqDocumentModalTabs activeTab={ state.activeTab }
        //     onTabClick={ (tabId) => this.onTabClick(tabId) } />
        //   <div className="hi-docmodal__tab-content" ref={ this.formScrollerRef }>
        //     { this.getTabContent(state.activeTab) }
        //   </div>
        // </div>
        this.getTabContent(state.activeTab)
      );
    } else {
      return (
        <div className="hi-docmodal__no-selection-message-container">
          <p className="hi-docmodal__no-selection-message">
            Please select an option from the dropdown menu above.
          </p>
        </div>
      )
    }
  }


  getPropertyOptions() {
    if (this.props.propertyChoices) {
      return this.props.propertyChoices.map(property => (
        <option value={ property.propertyKey } key={ property.propertyKey }>
          { property.address }
        </option>
      ));
    }
  }

  getPropertySelectBox(props: InqDocumentModalProps) {
    if (props.continuingWithoutProperty || this.isEditingExistingDocument()) {
      // To support editing of documents created through "Continue without a
      // property", we need to swap out the select box with something else
      const document = props.document;

      return (
        <span className="hi-docmodal__cwp-property-name">
          { document.propertyAddress || `Continue without a property` }
        </span>
      );
    } else {
      return (
        <select
          className={ cx('form-control hi-docmodal__property-select', {
            'blank': !(props.selectedProperty && props.selectedProperty.propertyKey)
          }) }
          placeholder="Select a property"
          value={ props.selectedProperty && props.selectedProperty.propertyKey || InquiryDocumentPropertySpecialOptions.EMPTY }
          onChange={ propertyKey => this.onPropertySelectionChange(propertyKey) }
          disabled={ this.isEditingExistingDocument() }
        >
          <option disabled value={ InquiryDocumentPropertySpecialOptions.EMPTY }>Select a property</option>
          <option value={ InquiryDocumentPropertySpecialOptions.CONTINUE_WITHOUT_PROPERTY }>Continue without a property</option>
          
          { this.getPropertyOptions() }
        </select>
      );
    }
  }

  getOfferTemplateSelectGroup() {
    const props = this.props;

    if (props.documentType === InquiryDocumentType.OFFER) {
      return (
        <div className="hi-docmodal__property-select-row">
          <label className="hi-docmodal__property-select-label">Selected property:</label>
          { this.getPropertySelectBox(props) }
        </div>
      );
    }
  }

  getContractTemplateSelectGroup() {
    const props = this.props;
    const templates = this.props.contractTemplates;

    if (props.documentType === InquiryDocumentType.CONTRACT) {
      return (
        <div className="hi-docmodal__property-select-row">
          <label className="hi-docmodal__property-select-label">Selected template:</label>
          <select
            className={ cx('form-control hi-docmodal__property-select', {
              // 'blank': !(props.selectedProperty && props.selectedProperty.propertyKey)
            }) }
            value={ props.selectedTemplateKey }
            onChange={ key => this.onContractTemplateSelectionChange(key) }
            disabled={ this.isEditingExistingDocument() }>
            <option disabled value={ InquiryDocumentPropertySpecialOptions.EMPTY }>Select a property or offer template</option>
            <option value={ InquiryDocumentPropertySpecialOptions.CONTINUE_WITHOUT_PROPERTY }>Continue without a property</option>

            { templates.map(template => (
              <option value={ template.loiKey || template.propertyKey }>
                { template.documentName || template.propertyAddress }
              </option>
            ))}
          </select>
        </div>
      );
    }
  }

  render(props: InqDocumentModalProps, state: InqDocumentModalState) {
    return (
      <HopplerModalizer onClose={ () => null }>
        <div className="hi-docmodal">
          <div className="hi-docmodal__title-bar">
            <span className="hi-docmodal__title-label">
              { this.getModalTitle() }
            </span>

            <button className="btn btn-link hi-docmodal__title-close" onClick={ props.onClose }>
              <i className="fas fa-times" /> Close
            </button>
          </div>
          
          { this.getOfferTemplateSelectGroup() }
          { this.getContractTemplateSelectGroup() }

          { this.getTabbedContainer() }
        </div>
      </HopplerModalizer>
    );
  }
}